import { useEffect, useRef, useState } from "react";
import useAuth from "../useAuth";
import useWebotServer from "../useWebotServer";
import { contactDocumentStore } from "./contactDocumentStore";
import { CONTACT_DOCUMENT_SOURCE } from "./contactDocumentSource";
import {
  contactGetScope,
  entryAnswersScope,
  fetchLatencyMs,
  firstReturnedCandidate,
  pollIntervalMs,
  shouldFetch,
  signalVerdict
} from "./contactDocumentRules";
import { createLookupBatcher } from "./contactLookupBatcher";
import { subscribeTabVisible } from "./tabVisibility";
import useContactBook from "./useContactBook";
import useContactSignal from "./useContactSignal";
import type { ContactDocumentEntry, ContactFetchTrigger } from "./contactDocumentStore";
import type { ContactReadOptions, ContactSignal } from "./contactDocumentRules";
import type { RealEstateContact, REST_schema } from "../../@types/sharedSchema";

/**
 * ONE contact's DOCUMENT, kept current without a listener on it — the reader side of the
 * real-time layer without sockets (`contactDocumentSource.ts`).
 *
 * ── How it stays current ────────────────────────────────────────────────────────────
 *
 *  1. On mount, the store is read. Nothing held (or held and stale, or a failed ask with
 *     nothing held) → `POST /contacts/get`. Two mounts of the same contact share the entry
 *     and the request.
 *  2. The contact's SIGNAL document is listened to (`useContactSignal`, one listener per
 *     contact however many mounts). When its `v` passes the version held, the document is
 *     fetched again; when it says `deleted`, the entry is marked absent with no fetch.
 *  3. When the tab becomes visible again, a stale entry is re-asked — a listener in a
 *     background tab may have been throttled.
 *  4. When the signal listener is in ERROR, the last answer is kept, `hasError` says so,
 *     and the document is polled every 30 s for as long as it is mounted.
 *
 * Writes made in this browser reach the entry through `useContactWrites` (the response's
 * `doc`, written through) before any signal lands, so a panel repaints in the response tick.
 *
 * ── `enabled` ───────────────────────────────────────────────────────────────────────
 *
 * For virtualised rows: a disabled reader neither asks nor listens, but still READS the
 * entry other readers filled, so scrolling back does not flash a spinner.
 *
 * ── The book, and the scope ─────────────────────────────────────────────────────────
 *
 * `bookKey` is the book the document lives in — the caller's own (`useContactBook().bookKey`:
 * the email, or the office's `org:` key for a pooled member) or a colleague's. Which SCOPE
 * the request asks under is derived: the own book asks under `mine`, any other under the
 * agency view — unless `read.scope` says `org`, which the office tab's panel needs for a
 * pooled record the viewer does not hold (`contactGetScope`).
 *
 * ── What it returns ─────────────────────────────────────────────────────────────────
 *
 *   doc: undefined   resolving — nobody has an answer yet (or the ask failed: `error`)
 *   doc: null        there is no such document in that book
 *   doc: contact     the document. The SAME object until the document is replaced, so an
 *                    effect that depends on it runs once per change, not once per render.
 *
 * The document is the server's: dates revived by the API client, NOT post-processed —
 * a reader that wants `postProcessContact`'s stamps clones and applies it, as the
 * listener-based readers always did.
 */

export type ContactDocument = {
  doc: RealEstateContact | null | undefined;
  /** `crm_contact.version` the document was read at; 0 while unknown. */
  version: number;
  /** A request is out right now (the first, or a refetch behind a held answer). */
  loading: boolean;
  /** The signal listener or the last fetch failed. The last answer, if any, is kept. */
  hasError: boolean;
  error: Error | null;
  /** The contact's current signal, or null while there is none. */
  signal: ContactSignal | null;
  /** How the last answer was prompted and how long it took (the shadow meter). */
  lastFetch?: ContactDocumentEntry["lastFetch"];
};

const RESOLVING: ContactDocument = {
  doc: undefined,
  version: 0,
  loading: false,
  hasError: false,
  error: null,
  signal: null
};

type FetchDocument = (request: REST_schema.CrmContactGetRequest) => Promise<REST_schema.CrmContactGetResponse>;
type FetchDocuments = (request: REST_schema.CrmContactGetManyRequest) => Promise<REST_schema.CrmContactGetManyResponse>;

/** The latest `contactsGet` — `useWebotServer()` rebuilds its functions on every render. */
let fetchDocument: FetchDocument | null = null;
/** The latest `contactsGetMany`; null where the API client has none, and then every phone is asked alone. */
let fetchDocuments: FetchDocuments | null = null;

export const bindDocumentFetcher = (fetcher: FetchDocument): void => {
  fetchDocument = fetcher;
};

export const bindDocumentsFetcher = (fetcher: FetchDocuments | null): void => {
  fetchDocuments = fetcher;
};

/**
 * Fetch ONE document by id into the store — a no-op when a request for it is already out.
 * `ownBook` is the caller's own book key (the scope comparison in `contactGetScope`);
 * `signal` is the one that prompted this, for the latency meter and for `signalV`.
 */
export const fetchContactDocument = async (
  bookKey: string,
  contactId: string,
  ownBook: string | null,
  trigger: ContactFetchTrigger,
  signal?: ContactSignal,
  read?: ContactReadOptions
): Promise<void> => {
  if (!contactDocumentStore.claim(bookKey, contactId)) return;
  // Captured when the request LEAVES: the token it travels with is the account's that is
  // signed in now, so this is the session its answer belongs to.
  const session = contactDocumentStore.session();
  const startedAt = Date.now();
  if (!fetchDocument) {
    contactDocumentStore.release(session, bookKey, [contactId], new Error("useContactDocument: no API client bound"));
    return;
  }
  try {
    const response = await fetchDocument({ contactId, ...contactGetScope(bookKey, ownBook, read) });
    contactDocumentStore.record(
      session,
      bookKey,
      contactId,
      response.found && response.doc ? { doc: response.doc, version: response.version } : null,
      {
        trigger,
        latencyMs: fetchLatencyMs(Date.now(), startedAt, signal?.at),
        signalV: signal?.v,
        scope: read?.scope ?? "mine"
      }
    );
  } catch (error) {
    // Released with the error, never recorded as "no such contact": that is a claim screens
    // act on (they offer to create one), and a failed request is not evidence for it.
    contactDocumentStore.release(session, bookKey, [contactId], error as Error);
  }
};

/** One phone waiting to be asked about: what it claimed, under which scope, for which session. */
type CandidatesAsk = {
  bookKey: string;
  candidates: readonly string[];
  claimed: readonly string[];
  scope: ReturnType<typeof contactGetScope>;
  session: number;
  done: () => void;
};

/** Phones in one `get-many`: at most 8 spellings each (the server's cap), so far under its 5,000 ids. */
export const CANDIDATE_PHONES_PER_REQUEST = 100;

/** One phone, one `/contacts/get { candidates }` — what every phone check sent before 2026-10-16. */
const askAlone = async (ask: CandidatesAsk): Promise<void> => {
  const startedAt = Date.now();
  if (!fetchDocument) {
    contactDocumentStore.release(ask.session, ask.bookKey, ask.claimed, new Error("useContactDocument: no API client bound"));
    return;
  }
  try {
    const response = await fetchDocument({ candidates: [...ask.candidates], ...ask.scope });
    contactDocumentStore.recordCandidates(
      ask.session,
      ask.bookKey,
      ask.candidates,
      response.found && response.doc && response.contactId
        ? { contactId: response.contactId, doc: response.doc, version: response.version }
        : null,
      { trigger: "candidates", latencyMs: fetchLatencyMs(Date.now(), startedAt, undefined) }
    );
  } catch (error) {
    contactDocumentStore.release(ask.session, ask.bookKey, ask.claimed, error as Error);
  }
};

/**
 * Several phones of the OWN book in ONE `get-many` carrying every spelling of every phone.
 * Each phone takes the first of its own spellings that came back (`firstReturnedCandidate`,
 * the server's rule for one phone's `candidates`), so it is answered exactly as if it had
 * been asked alone. A failed request is a failure for every phone in it, never "not a contact".
 */
const askTogether = async (fetchMany: FetchDocuments, asks: readonly CandidatesAsk[]): Promise<void> => {
  const startedAt = Date.now();
  try {
    const response = await fetchMany({ contactIds: [...new Set(asks.flatMap((ask) => ask.candidates))] });
    const returned = new Map(response.contacts.map((entry) => [entry.contactId, entry]));
    const latencyMs = fetchLatencyMs(Date.now(), startedAt, undefined);
    for (const ask of asks) {
      const hit = firstReturnedCandidate(ask.candidates, returned);
      contactDocumentStore.recordCandidates(
        ask.session,
        ask.bookKey,
        ask.candidates,
        hit ? { contactId: hit.contactId, doc: hit.entry.doc, version: hit.entry.version } : null,
        { trigger: "candidates", latencyMs }
      );
    }
  } catch (error) {
    for (const ask of asks) contactDocumentStore.release(ask.session, ask.bookKey, ask.claimed, error as Error);
  }
};

/** Asks queued for the batcher, by the key it carries for them. */
const queuedAsks = new Map<string, CandidatesAsk>();
let askSequence = 0;

/**
 * Three requests out at once, as the name lookups. A chunk holds its slot until its request
 * settles or {@link CANDIDATES_REQUEST_DEADLINE_MS} passes, whichever comes first: the server's
 * own timeout is 900 s on Cloud Run, and three requests stuck that long would leave every card
 * of the tab on "resolving" for fifteen minutes. In two days of these reads none took more
 * than 7 s.
 */
const createCandidatesBatcher = () =>
  createLookupBatcher({
    chunkSize: CANDIDATE_PHONES_PER_REQUEST,
    concurrency: 3,
    send: sendCandidateAsks
  });

/** How long a request about phones may stay unanswered before its slot is given back and its cards are told it failed. */
export const CANDIDATES_REQUEST_DEADLINE_MS = 20 * 1000;

async function sendCandidateAsks(keys: string[]): Promise<void> {
  const asks = keys.flatMap((key) => {
    const ask = queuedAsks.get(key);
    queuedAsks.delete(key);
    return ask ? [ask] : [];
  });
  let deadline: ReturnType<typeof setTimeout> | undefined;
  try {
    // Queued before an account switch: that account's store is gone, and its phones are not
    // asked about under the next account's token.
    const live = asks.filter((ask) => ask.session === contactDocumentStore.session());
    const byBook = new Map<string, CandidatesAsk[]>();
    live.forEach((ask) => byBook.set(ask.bookKey, [...(byBook.get(ask.bookKey) ?? []), ask]));
    const fetchMany = fetchDocuments;
    const answered = Promise.all(
      [...byBook.values()].map((group) =>
        group.length > 1 && fetchMany ? askTogether(fetchMany, group) : Promise.all(group.map(askAlone))
      )
    ).then(() => true);
    const late = new Promise<false>((resolve) => {
      deadline = setTimeout(() => resolve(false), CANDIDATES_REQUEST_DEADLINE_MS);
    });
    if (!(await Promise.race([answered, late]))) {
      // Released with an error, like any failed request: the cards say so and ask again on the
      // next prompt. If the answer still arrives, it settles only what is still being asked.
      const error = new Error(`useContactDocument: no answer about these phones in ${CANDIDATES_REQUEST_DEADLINE_MS / 1000} s`);
      live.forEach((ask) => contactDocumentStore.release(ask.session, ask.bookKey, ask.claimed, error));
    }
  } finally {
    if (deadline !== undefined) clearTimeout(deadline);
    asks.forEach((ask) => ask.done());
  }
}

let candidatesBatcher = createCandidatesBatcher();

/**
 * TESTS ONLY: a fresh batcher. A test that leaves a request unanswered on purpose holds one of
 * the module's three slots for the rest of the file.
 */
export const resetCandidatesBatcherForTests = (): void => {
  candidatesBatcher.clear();
  queuedAsks.clear();
  candidatesBatcher = createCandidatesBatcher();
};

/**
 * Fetch a phone by every spelling it could be filed under (`getPhoneCandidates`, in order
 * of preference). The server answers which spelling exists; the store learns the answer
 * for every spelling (`recordCandidates`). Spellings already being asked about are left to
 * that request.
 *
 * PHONES ASKED IN THE SAME TICK LEAVE TOGETHER (`createLookupBatcher`, as the name lookups
 * do). A screen of cards used to send one request per phone: the listings board mounts a
 * seller badge per listing (200 a page), and one board sent up to 317 requests in a minute,
 * answered in 0.5 to 2 s inside the burst against 35 ms alone (measured 2026-10-16). Now the
 * own book's phones asked before the end of the tick go in ONE `get-many` with every
 * spelling (100 phones a request, three requests at a time). A phone asked ALONE still sends
 * its own `/contacts/get { candidates }`, exactly as before, and so does a question about
 * any book but the caller's own (a `get-many` there is an export, behind the export gates).
 */
export const fetchContactCandidates = (
  bookKey: string,
  candidates: readonly string[],
  ownBook: string | null
): Promise<void> => {
  const claimed = contactDocumentStore.claimMany(bookKey, candidates);
  if (claimed.length === 0) return Promise.resolve();
  const session = contactDocumentStore.session();
  const scope = contactGetScope(bookKey, ownBook);
  if (Object.keys(scope).length > 0) {
    return askAlone({ bookKey, candidates: [...candidates], claimed, scope, session, done: () => undefined });
  }
  return new Promise<void>((resolve) => {
    askSequence += 1;
    const key = String(askSequence);
    queuedAsks.set(key, { bookKey, candidates: [...candidates], claimed, scope, session, done: resolve });
    candidatesBatcher.request([key]);
  });
};

const NO_IDS: readonly string[] = [];

/** What a reader renders from: the store's epoch and the entries it reads, by identity. */
const readerSnapshot = (bookKey: string | null, contactIds: readonly string[]): readonly unknown[] => [
  contactDocumentStore.epoch(),
  ...(bookKey ? contactIds.map((contactId) => contactDocumentStore.get(bookKey, contactId)) : NO_IDS)
];

const sameSnapshot = (a: readonly unknown[], b: readonly unknown[]): boolean =>
  a.length === b.length && a.every((value, index) => value === b[index]);

/**
 * Re-renders the caller when an entry IT READS changes — `contactIds` in `bookKey` — or when
 * the store is invalidated (the epoch moves). Also binds the store to the signed-in account
 * during render, and idempotently, so the first frame after a sign-in cannot show the
 * previous user's contact. A subscriber set plus a check: this app is on React 17, which has
 * no `useSyncExternalStore`.
 *
 * NOT on every change of the store. It was, and every answer re-rendered every mounted
 * reader: the listings board mounts a seller badge per listing (200 a page) and each one asks
 * about its own phone, so a page of answers cost 200 × 200 renders where the Firestore
 * listener costs 200 (measured in jsdom on 2026-10-15: 40,200 renders and about 8 s, against
 * 200 and 0.08 s). The store puts a NEW entry object in on every change of that entry, so
 * "does this reader have anything new to show" is an identity comparison over the few entries
 * it reads.
 */
export const useContactDocumentStoreSubscription = (
  bookKey: string | null,
  contactIds: readonly string[]
): string | null => {
  const { user } = useAuth();
  const account = (user?.email as string | undefined) ?? null;
  contactDocumentStore.bindAccount(account);

  const { contactsGet, contactsGetMany } = useWebotServer();
  const fetcher = useRef(contactsGet);
  fetcher.current = contactsGet;
  bindDocumentFetcher((request) => fetcher.current(request));
  const manyFetcher = useRef(contactsGetMany);
  manyFetcher.current = contactsGetMany;
  bindDocumentsFetcher(typeof contactsGetMany === "function" ? (request) => manyFetcher.current(request) : null);

  // What THIS render read, and what to read again on a change — refs, so the one
  // subscription below always compares against the latest render.
  const rendered = useRef<readonly unknown[]>(NO_IDS);
  rendered.current = readerSnapshot(bookKey, contactIds);
  const watched = useRef({ bookKey, contactIds });
  watched.current = { bookKey, contactIds };

  const [, setVersion] = useState(0);
  useEffect(() => {
    const onChange = (): void => {
      const { bookKey: book, contactIds: ids } = watched.current;
      if (!sameSnapshot(readerSnapshot(book, ids), rendered.current)) setVersion((value) => value + 1);
    };
    const unsubscribe = contactDocumentStore.subscribe(onChange);
    // A change between the render and this subscription is caught now, not at the next one.
    onChange();
    return unsubscribe;
  }, []);

  return account;
};

export default function useContactDocument(
  bookKey: string | null,
  contactId: string | null,
  enabled = true,
  read?: ContactReadOptions
): ContactDocument {
  const account = useContactDocumentStoreSubscription(bookKey, contactId ? [contactId] : NO_IDS);
  // The caller's OWN book, for the scope comparison: the email until the server has
  // answered (right for every personal book), the office's key for a pooled member.
  const { ownBookKey } = useContactBook();
  const ownBook = ownBookKey ?? account;
  const addressed = Boolean(account && bookKey && contactId);
  const active = addressed && enabled;
  const scope = read?.scope ?? "mine";
  const readOrgId = read?.orgId ?? null;

  // Moves when the store is invalidated (an import, a bulk edit, a re-key): every mounted
  // reader re-checks what it holds, and only what is stale is fetched.
  const epoch = contactDocumentStore.epoch();
  const entry = addressed ? contactDocumentStore.get(bookKey!, contactId!) : undefined;
  const signalState = useContactSignal(bookKey, contactId, active);

  // 1. Mount, invalidation, a reader becoming active — and an answer that is no answer for
  //    THIS reader's scope (`entryAnswersScope`: a `mine` absence, read under `org`), also one
  //    that lands while this reader is mounted: `heldAt` moves with every answer, and with
  //    nothing else (a failed ask keeps it, so a server that is down is not asked in a loop —
  //    `error` stops the mismatch leg for the same reason; a signal, the tab coming back or an
  //    invalidation retries).
  const heldAt = entry?.fetchedAt ?? 0;
  useEffect(() => {
    if (!active) return;
    const held = contactDocumentStore.get(bookKey!, contactId!);
    const mismatch = Boolean(held && !held.inFlight && !held.error && !entryAnswersScope(held, scope));
    if (shouldFetch(held, Date.now()) || mismatch) {
      void fetchContactDocument(
        bookKey!,
        contactId!,
        ownBook,
        held ? "stale" : "mount",
        undefined,
        { scope, orgId: readOrgId }
      );
    }
  }, [active, bookKey, contactId, ownBook, epoch, scope, readOrgId, heldAt]);

  // 2. The signal. Re-evaluated when the signal moves AND when a request lands (`inFlight`):
  //    a request that was out when the signal arrived may have read the document before
  //    the write the signal is about, so the verdict is asked again once it is in. Each
  //    signal is acted on ONCE, though (`actedSignal` — the signal under its address): a
  //    fetch that FAILS also flips `inFlight`, and the verdict would still read "fetch" —
  //    a server that is down would be asked in a tight loop. The next signal, the tab
  //    coming back, an invalidation or a remount retries.
  //
  //    A delete counts as acted on too, for two reasons. The server starts a re-created
  //    contact again at `v: 1`, so a contact deleted and re-created twice signals `1`, a
  //    delete, `1` — a guard that remembered fetches only read the second `1` as already
  //    tried, and left the card on "not a contact" for a contact that exists. And once a
  //    delete has been applied, any LATER answer that says "present" — an own re-create's
  //    `put`, a fetch of the next life — is newer than that signal: re-applying the delete
  //    over it put the panel back on "not a contact" until the next life's own signal landed.
  const signal = signalState.signal;
  const signalKey = signal ? `${bookKey}\n${contactId}\n${signal.v}:${signal.deleted}` : null;
  const inFlight = entry?.inFlight ?? false;
  const actedSignal = useRef<string | null>(null);
  useEffect(() => {
    if (!active || !signal || actedSignal.current === signalKey) return;
    const verdict = signalVerdict(signal, contactDocumentStore.get(bookKey!, contactId!));
    if (verdict === "none") return;
    actedSignal.current = signalKey;
    if (verdict === "absent") contactDocumentStore.markAbsent(bookKey!, [contactId!], signal.v);
    else void fetchContactDocument(bookKey!, contactId!, ownBook, "signal", signal, { scope, orgId: readOrgId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, bookKey, contactId, ownBook, signalKey, inFlight]);

  // 3. The tab coming back — told to every reader at once (`subscribeTabVisible`).
  useEffect(() => {
    if (!active) return undefined;
    return subscribeTabVisible(() => {
      if (shouldFetch(contactDocumentStore.get(bookKey!, contactId!), Date.now())) {
        void fetchContactDocument(bookKey!, contactId!, ownBook, "visibility", undefined, { scope, orgId: readOrgId });
      }
    });
  }, [active, bookKey, contactId, ownBook, scope, readOrgId]);

  // 4. The listener in error: poll — in 'server' mode only, where the signal is the one
  //    thing keeping the document current. In 'shadow' mode the Firestore listener on the
  //    document still paints every screen, and a signal rule that is not deployed yet
  //    (permission-denied on every signal) must not turn every open card into a 30 s poll.
  const pollMs = active && CONTACT_DOCUMENT_SOURCE === "server" ? pollIntervalMs(signalState.error) : null;
  useEffect(() => {
    if (!pollMs) return undefined;
    const timer = setInterval(
      () => void fetchContactDocument(bookKey!, contactId!, ownBook, "poll", undefined, { scope, orgId: readOrgId }),
      pollMs
    );
    return () => clearInterval(timer);
  }, [pollMs, bookKey, contactId, ownBook, scope, readOrgId]);

  if (!addressed) return RESOLVING;

  const fetchError = entry?.error ?? null;
  // A `mine` absence is no answer for an `org` reader: resolving until its own lands.
  return {
    doc: entry?.state === "present" ? entry.doc : entry?.state === "absent" && entryAnswersScope(entry, scope) ? null : undefined,
    version: entry?.version ?? 0,
    loading: inFlight,
    hasError: Boolean(fetchError || signalState.error),
    error: fetchError ?? signalState.error,
    signal,
    lastFetch: entry?.lastFetch
  };
}
